export type CanvasSize = {
  width: number;
  height: number;
};

export type CSSDimensions = {
  width: number | string;
  height: number | string;
};

export type ElementOffset = {
  left: number;
  top: number;
};

export const CANVAS_DATA_ATTRIBUTE = 'data-editor-canvas';

export const setCanvasDimensions = (
  el: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D | null | undefined,
  { width, height }: CanvasSize,
  retinaScaling = 1,
): void => {
  if (!el) {
    return;
  }
  el.width = width;
  el.height = height;
  if (retinaScaling > 1) {
    el.setAttribute?.('width', `${width * retinaScaling}`);
    el.setAttribute?.('height', `${height * retinaScaling}`);
    if (ctx) {
      ctx.scale(retinaScaling, retinaScaling);
    }
  }
};

export const setCSSDimensions = (
  el: HTMLElement,
  { width, height }: Partial<CSSDimensions>,
): void => {
  if (!el || !el.style) {
    return;
  }
  if (width !== undefined) {
    el.style.width = typeof width === 'number' ? `${width}px` : width;
  }
  if (height !== undefined) {
    el.style.height = typeof height === 'number' ? `${height}px` : height;
  }
};

const getDocumentFromElement = (element: HTMLElement): Document | null =>
  element.ownerDocument ?? null;

const getWindowFromElement = (
  element: HTMLElement,
): (Window & typeof globalThis) | null => element.ownerDocument?.defaultView ?? null;

const getScrollLeftTop = (element: HTMLElement | null): ElementOffset => {
  const doc = element && getDocumentFromElement(element);
  let left = 0;
  let top = 0;
  if (!element || !doc) {
    return { left, top };
  }
  let elementLoop: HTMLElement | Document | ShadowRoot = element;
  const docElement = doc.documentElement;
  const body = doc.body;
  while (
    elementLoop &&
    (elementLoop.parentNode || (elementLoop as ShadowRoot).host)
  ) {
    elementLoop = (elementLoop.parentNode ||
      (elementLoop as ShadowRoot).host) as
      | HTMLElement
      | Document
      | ShadowRoot;
    if (elementLoop === doc) {
      left = body?.scrollLeft || docElement.scrollLeft || 0;
      top = body?.scrollTop || docElement.scrollTop || 0;
    } else {
      left += (elementLoop as HTMLElement).scrollLeft || 0;
      top += (elementLoop as HTMLElement).scrollTop || 0;
    }

    if (
      elementLoop.nodeType === 1 &&
      (elementLoop as HTMLElement).style.position === 'fixed'
    ) {
      break;
    }
  }

  return { left, top };
};

/**
 * Returns offset for a given element.
 */
export function getElementOffset(element: HTMLElement): ElementOffset {
  const doc = element && getDocumentFromElement(element);
  const offset = { left: 0, top: 0 };

  if (!doc) {
    return offset;
  }
  const elementStyle =
    getWindowFromElement(element)?.getComputedStyle(element, null) || null;
  offset.left += parseInt(elementStyle?.borderLeftWidth ?? '0', 10) || 0;
  offset.top += parseInt(elementStyle?.borderTopWidth ?? '0', 10) || 0;
  offset.left += parseInt(elementStyle?.paddingLeft ?? '0', 10) || 0;
  offset.top += parseInt(elementStyle?.paddingTop ?? '0', 10) || 0;

  let box = { left: 0, top: 0 };

  const docElem = doc.documentElement;
  if (typeof element.getBoundingClientRect !== 'undefined') {
    box = element.getBoundingClientRect();
  }

  const scrollLeftTop = getScrollLeftTop(element);

  return {
    left:
      box.left + scrollLeftTop.left - (docElem.clientLeft || 0) + offset.left,
    top: box.top + scrollLeftTop.top - (docElem.clientTop || 0) + offset.top,
  };
}
